import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, DollarSign, Users, CreditCard, AlertCircle } from "lucide-react";
import { apiGet } from "./api";
import AdminPageLayout from "./AdminPageLayout";

interface Subscription {
  id: number;
  advertiser_id: number;
  stripe_customer_id: string;
  stripe_subscription_id: string;
  plan_id: string;
  status: string;
  current_period_end: string;
  created_at: string;
  business_name: string;
  contact_email: string;
  region: string;
  ads_count: number;
  active_ads_count: number;
}

interface Invoice {
  id: number;
  subscription_id: number;
  stripe_invoice_id: string;
  amount_cents: number;
  currency: string;
  status: string;
  period_start: string;
  period_end: string;
  created_at: string;
  plan_id: string;
  business_name: string;
  contact_email: string;
}

interface BillingEvent {
  id: number;
  stripe_event_id: string;
  type: string;
  status: string;
  payload_hash: string;
  error_message: string | null;
  processed_at: string;
}

export default function AdminBilling() {
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [events, setEvents] = useState<BillingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, canceled: 0, past_due: 0 });
  const [invoiceStats, setInvoiceStats] = useState({ total_count: 0, total_amount: 0 });
  const [eventStats, setEventStats] = useState({ total: 0, success: 0, failure: 0, processing: 0 });
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    fetchData();
  }, [statusFilter, searchQuery]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch subscriptions
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (searchQuery) params.set("query", searchQuery);

      const subsData = await apiGet<{
        ok: boolean;
        items: Subscription[];
        stats: { total: number; active: number; canceled: number; past_due: number };
      }>(`/api/admin/billing?${params}`);

      setSubs(subsData.items || []);
      setStats({
        total: subsData.stats?.total || 0,
        active: subsData.stats?.active || 0,
        canceled: subsData.stats?.canceled || 0,
        past_due: subsData.stats?.past_due || 0
      });

      // Fetch invoices
      const invoicesData = await apiGet<{
        ok: boolean;
        items: Invoice[];
        total: { total_count: number; total_amount: number };
      }>("/api/admin/billing/invoices");

      setInvoices(invoicesData.items || []);
      setInvoiceStats({
        total_count: invoicesData.total?.total_count || 0,
        total_amount: invoicesData.total?.total_amount || 0
      });

      // Fetch events
      const eventsData = await apiGet<{
        ok: boolean;
        items: BillingEvent[];
        stats: { total: number; success: number; failure: number; processing: number };
      }>("/api/admin/billing/events");

      setEvents(eventsData.items || []);
      setEventStats({
        total: eventsData.stats?.total || 0,
        success: eventsData.stats?.success || 0,
        failure: eventsData.stats?.failure || 0,
        processing: eventsData.stats?.processing || 0
      });
    } catch (error) {
      console.error("[billing] Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents: number, currency = "AUD") => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: currency,
    }).format(cents / 100);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      active: "default",
      paid: "default",
      success: "default",
      canceled: "secondary",
      past_due: "destructive",
      failure: "destructive",
      processing: "secondary",
    };
    return (
      <Badge variant={variants[status] || "secondary"} data-testid={`badge-status-${status}`}>
        {status}
      </Badge>
    );
  };

  return (
    <AdminPageLayout
      title="Billing & Subscriptions"
      description="Manage advertiser subscriptions, invoices, and webhook events"
      icon={<CreditCard className="w-8 h-8" />}
      loading={loading}
    >
      <div className="space-y-6" data-testid="page-admin-billing">

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card data-testid="card-stats-total">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Subscriptions</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-stats-total">{stats.total}</div>
          </CardContent>
        </Card>

        <Card data-testid="card-stats-active">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <CreditCard className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600" data-testid="text-stats-active">
              {stats.active}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="card-stats-revenue">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-stats-revenue">
              {formatCurrency(invoiceStats.total_amount)}
            </div>
            <p className="text-xs text-muted-foreground">
              {invoiceStats.total_count} invoices
            </p>
          </CardContent>
        </Card>

        <Card data-testid="card-stats-past-due">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Past Due</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600" data-testid="text-stats-past-due">
              {stats.past_due}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="subscriptions" className="space-y-4">
        <TabsList data-testid="tabs-billing">
          <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">
            Subscriptions
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            Invoices
          </TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">
            Webhook Events
          </TabsTrigger>
        </TabsList>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Subscriptions</CardTitle>
              <CardDescription>Advertiser subscription management</CardDescription>
              <div className="flex gap-2 pt-4">
                <Input
                  placeholder="Search by business or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="max-w-xs"
                  data-testid="input-search-subscriptions"
                />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="select-status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="canceled">Canceled</SelectItem>
                    <SelectItem value="past_due">Past Due</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={fetchData}
                  data-testid="button-refresh"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Period End</TableHead>
                    <TableHead>Ads</TableHead>
                    <TableHead>Region</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground">
                        No subscriptions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    subs.map((s) => (
                      <TableRow key={s.id} data-testid={`row-subscription-${s.id}`}>
                        <TableCell className="font-mono text-sm">{s.id}</TableCell>
                        <TableCell className="font-medium">{s.business_name || "-"}</TableCell>
                        <TableCell className="text-sm">{s.contact_email || "-"}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{s.plan_id || "-"}</Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(s.status)}</TableCell>
                        <TableCell className="text-sm">
                          {formatDate(s.current_period_end)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {s.active_ads_count}/{s.ads_count}
                        </TableCell>
                        <TableCell className="text-sm">{s.region || "ALL"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Invoices</CardTitle>
              <CardDescription>Payment history and invoice records</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No invoices found
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoices.slice(0, 20).map((inv) => (
                      <TableRow key={inv.id} data-testid={`row-invoice-${inv.id}`}>
                        <TableCell className="font-mono text-sm">{inv.id}</TableCell>
                        <TableCell className="font-medium">{inv.business_name || "-"}</TableCell>
                        <TableCell className="font-semibold">
                          {formatCurrency(inv.amount_cents, inv.currency)}
                        </TableCell>
                        <TableCell>{getStatusBadge(inv.status)}</TableCell>
                        <TableCell className="text-sm">
                          {formatDate(inv.period_start)} - {formatDate(inv.period_end)}
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(inv.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Webhook Events</CardTitle>
              <CardDescription>
                Stripe webhook event processing log (Success: {eventStats.success}, Failures: {eventStats.failure})
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Event ID</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead>Processed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No webhook events found
                      </TableCell>
                    </TableRow>
                  ) : (
                    events.slice(0, 30).map((evt) => (
                      <TableRow key={evt.id} data-testid={`row-event-${evt.id}`}>
                        <TableCell className="font-mono text-sm">{evt.id}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {evt.stripe_event_id.substring(0, 20)}...
                        </TableCell>
                        <TableCell className="text-sm">{evt.type}</TableCell>
                        <TableCell>{getStatusBadge(evt.status)}</TableCell>
                        <TableCell className="text-xs text-red-600">
                          {evt.error_message ? evt.error_message.substring(0, 50) : "-"}
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(evt.processed_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>
    </AdminPageLayout>
  );
}
